import React, { useCallback, useEffect, type FC } from 'react';
import { styled } from '@compiled/react';
import noop from 'lodash/noop';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import TaskIcon from '@atlaskit/icon/core/migration/task';
import Lozenge from '@atlaskit/lozenge';
import { token } from '@atlaskit/tokens';
import { COMMAND_PALETTE_ISSUE_ACTIONS } from '@atlassian/jira-command-palette-common/src/common/constants.tsx';
import { getCommandPaletteIssueActionsPriority } from '@atlassian/jira-command-palette-common/src/common/utils/get-command-palette-issue-actions-priority/index.tsx';
import { getCommandPaletteIssueActionsHeader } from '@atlassian/jira-command-palette-common/src/common/utils/index.tsx';
import { useCommandKeywords } from '@atlassian/jira-command-palette-common/src/controllers/use-command-keywords/index.tsx';
import { CommandFieldHeader } from '@atlassian/jira-command-palette-issue-actions-common/src/common/ui/command-field-header/index.tsx';
import { COMMAND_PALETTE_REGISTRY_IDS } from '@atlassian/jira-command-palette-registry/src/common/constants/registry/index.tsx';
import { useSubscribeCommands } from '@atlassian/jira-command-palette-registry/src/controllers/use-subscribe-commands/index.tsx';
import {
	type ChildResult,
	type HeaderProps,
	type Command,
	CommandActionType,
} from '@atlassian/jira-command-palette/src/common/types/commands/index.tsx';
import { useCommandPaletteExpandedCommand } from '@atlassian/jira-command-palette/src/controllers/command-palette/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { useIntl } from '@atlassian/jira-intl';
import { FIELD_TYPE_MAP } from '@atlassian/jira-issue-analytics/src/services/update-issue-field/constants.tsx';
import { getUpdateAnalyticsFlowHelper } from '@atlassian/jira-issue-analytics/src/services/update-issue-field/index.tsx';
import { useIssueKey } from '@atlassian/jira-issue-context-service/src/main.tsx';
import { useIssueAri } from '@atlassian/jira-issue-hooks/src/services/use-issue-ari/index.tsx';
import { STATUS_TYPE } from '@atlassian/jira-platform-field-config/src/index.tsx';
import { fireTrackAnalytics } from '@atlassian/jira-product-analytics-bridge';
import { getAriConfig, isAri } from '@atlassian/jira-platform-ari/src/index.tsx';
import { STATUS_COMMAND_ID } from './constants.tsx';
import { messages } from './messages.tsx';
import type { StatusViewCommandProps } from './types.tsx';
import { useSaveStatusField } from './use-save-status-field/index.tsx';
import { getTransitionKeywords } from './utils.tsx';

export const CommandPaletteStatusView: FC<StatusViewCommandProps> = ({
	onFetch,
	wasFetchSuccessful,
	currentStatus,
	transitions,
	renderTransition,
	onSuccess,
	onUpdateStatusMutation,
}) => {
	const issueKey = useIssueKey();
	const { formatMessage } = useIntl();
	const { getKeywords } = useCommandKeywords();
	const subscribeCommands = useSubscribeCommands();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const [{ expandedCommand, isOpen }, { setChildCommands }] = useCommandPaletteExpandedCommand();

	const renderHeader = useCallback(
		(base: HeaderProps) => <CommandFieldHeader {...base} issueKey={issueKey} />,
		[issueKey],
	);

	const issueAri = useIssueAri();
	const { saveStatusField } = useSaveStatusField(issueKey, issueAri, onUpdateStatusMutation);
	const expandedCommandId = expandedCommand?.id;

	const issueId =
		issueAri && isAri(issueAri) && fg('one_event_improvements_1')
			? getAriConfig(issueAri).resourceId
			: undefined;

	const loadTransitions = useCallback((): ChildResult => {
		if (!wasFetchSuccessful) return { commands: [] };

		const transitionCommands: Command[] = transitions.map((transition) => ({
			id: `${STATUS_COMMAND_ID}-${transition.id}`,
			name: transition.name,
			components: {
				Name: () => (
					<TransitionWrapper>
						{renderTransition({ transition }, { addTransitionArrowSpacing: true })}
					</TransitionWrapper>
				),
				LeftIcon: !expandedCommandId ? () => <TaskIcon spacing="spacious" label="" /> : undefined,
			},
			keywords: getTransitionKeywords(transition),
			primaryAction: {
				type: CommandActionType.PERFORM,
				perform: () => {
					fireTrackAnalytics(createAnalyticsEvent({}), 'issueStatus updated', {
						oldValId: currentStatus.id,
						oldStatusCategoryId: currentStatus.statusCategory.id,
						newValId: transition.to.id,
						newStatusCategoryId: transition.to.statusCategory.id,
						hasScreen: transition.hasScreen,
						isConditional: transition.isConditional,
					});
					getUpdateAnalyticsFlowHelper().fireAnalyticsEnd(STATUS_TYPE, {
						analytics: createAnalyticsEvent({}),
						attributes: {
							isCommandPaletteEditing: true,
							fieldType: FIELD_TYPE_MAP[STATUS_TYPE],
							oldValId: currentStatus.id,
							oldStatusCategoryId: currentStatus.statusCategory.id,
							newValId: transition.to.id,
							newStatusCategoryId: transition.to.statusCategory.id,
							...(fg('one_event_improvements_1') && {
								issueId,
							}),
						},
					});

					saveStatusField(transition, () => {
						onSuccess?.(transition.to, false, createAnalyticsEvent({}));
					});
				},
			},
			analytics: {
				action: 'changeStatus',
				actionCategory: COMMAND_PALETTE_ISSUE_ACTIONS,
			},
		}));

		const currentCommand: Command[] = expandedCommandId
			? [
					{
						id: `${STATUS_COMMAND_ID}-${currentStatus.id}-current`,
						name: currentStatus.name,
						components: {
							LeftIcon: !expandedCommandId
								? () => <TaskIcon spacing="spacious" label={formatMessage(messages.editStatus)} />
								: undefined,
							Name: () => (
								<TransitionWrapper>
									<SelectedStatusContainer>
										<Lozenge isBold>{currentStatus.name}</Lozenge>
									</SelectedStatusContainer>
									<CurrentStatusContainer>{formatMessage(messages.current)}</CurrentStatusContainer>
								</TransitionWrapper>
							),
						},
						primaryAction: {
							type: CommandActionType.PERFORM,
							perform: noop,
						},
					},
				]
			: [];

		const commands: Command[] = [...currentCommand, ...transitionCommands];

		return { commands };
	}, [
		wasFetchSuccessful,
		transitions,
		expandedCommandId,
		currentStatus.id,
		currentStatus.name,
		currentStatus.statusCategory.id,
		renderTransition,
		createAnalyticsEvent,
		saveStatusField,
		issueId,
		onSuccess,
		formatMessage,
	]);

	useEffect(() => {
		const { commands } = loadTransitions();

		const statusCommand: Command = {
			id: STATUS_COMMAND_ID,
			name: formatMessage(messages.editStatus),
			shortcut: 'd',
			placeholder: formatMessage(messages.searchTransition),
			keywords: getKeywords('changeStatusSynonyms'),
			section: getCommandPaletteIssueActionsHeader(issueKey),
			priority: getCommandPaletteIssueActionsPriority('STATUS'),
			components: {
				LeftIcon: () => <TaskIcon spacing="spacious" label="" />,
			},
			primaryAction: {
				type: CommandActionType.COMMAND_LIST,
				fallback: {
					onNoResults: {
						label: formatMessage(messages.fallbackNoResults),
					},
				},
				components: {
					Header: renderHeader,
				},
				commands,
				onClick: () => {
					getUpdateAnalyticsFlowHelper().fireAnalyticsStart(STATUS_TYPE, {
						analytics: createAnalyticsEvent({}),
						attributes: {
							fieldType: FIELD_TYPE_MAP[STATUS_TYPE],
							isCommandPaletteEditing: true,
						},
					});
				},
			},
			analytics: {
				action: 'changeStatusMenu',
			},
		};

		const unsubscribe = subscribeCommands(COMMAND_PALETTE_REGISTRY_IDS.STATUS, [statusCommand]);

		return () => unsubscribe();
	}, [
		formatMessage,
		getKeywords,
		issueKey,
		loadTransitions,
		renderHeader,
		subscribeCommands,
		expandedCommand?.id,
		createAnalyticsEvent,
	]);

	useEffect(() => {
		if (isOpen && onFetch) {
			onFetch(createAnalyticsEvent({}));
		}
	}, [isOpen, onFetch, createAnalyticsEvent]);

	useEffect(() => {
		if (isOpen && expandedCommandId === STATUS_COMMAND_ID) {
			setChildCommands(loadTransitions());
		}
	}, [expandedCommandId, isOpen, loadTransitions, setChildCommands]);

	return null;
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const TransitionWrapper = styled.div({
	display: 'flex',
	justifyContent: 'space-between',
	marginLeft: token('space.050'),
	height: '24px',
	alignItems: 'center',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const SelectedStatusContainer = styled.div({
	flexGrow: 1,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const CurrentStatusContainer = styled.div({
	color: token('color.text.subtle'),
});
